<script setup lang="ts">
import { useOnTimezoneChange } from '@entities/viewer'

import { DisciplineFilesModal } from '../model/enums'
import { useDisciplineFilesStore } from '../model/files/store'
import { abortDisciplineFilesApi, useDisciplineFilesApi } from '../api/files.hook'
import DisciplinePageLoader from './DisciplinePageLoader.vue'
import TableDisciplineFiles from './TableDisciplineFiles.vue'
import ModalDisciplineFilesSend from './modals/ModalDisciplineFilesSend.vue'
import ModalDisciplineFilePack from './modals/ModalDisciplineFilePack.vue'
import ModalDisciplineFilePackDelete from './modals/ModalDisciplineFilePackDelete.vue'
import ModalDisciplineCertificate from './modals/ModalDisciplineCertificate.vue'

defineOptions({
  name: 'DisciplineFilesPage',
})

const route = useRoute()
const store = useDisciplineFilesStore()

/** The first list read shares the page loader with the discipline, so the tab appears with its rows in place; a
 *  Search or a page change keeps the rows and only locks the controls. */
const refIsFirstLoad = computed<boolean>(() => store.isLoading && store.getMeta === null)

const PACK_HASH_PATTERN = /^#(\d+)$/

const fetchFiles = (): void => {
  void useDisciplineFilesApi({ disciplineId: route.params.id as string })
}

/** The tasks tab links a pack as `#<packId>`, as the legacy page did. */
const openPackFromHash = (): void => {
  const packId = PACK_HASH_PATTERN.exec(route.hash)?.[1]
  if (packId) store.openModal(DisciplineFilesModal.Pack, Number(packId))
}

onMounted(() => {
  fetchFiles()
  openPackFromHash()
})

// Filters and pagination navigate by query; the middleware has already written them to the store.
watch(() => route.query, () => {
  if (route.name === 'disciplines-id-files') fetchFiles()
})

// Dates arrive pre-shifted into the admin's view timezone, so a timezone change needs a refetch.
useOnTimezoneChange(fetchFiles)

onBeforeUnmount(() => {
  abortDisciplineFilesApi()
  store.closeModal()
  store.resetList()
})
</script>

<template>
  <DisciplinePageLoader :loading="refIsFirstLoad" />
  <TableDisciplineFiles />
  <ModalDisciplineFilesSend />
  <ModalDisciplineFilePack />
  <ModalDisciplineFilePackDelete />
  <ModalDisciplineCertificate />
</template>
